import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  Zap,
  FileText,
  BookOpen,
  Star,
  PoundSterling,
  ArrowRight,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/quoting-software-for-builders";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Quoting Software for Builders",
  "description": "Quoting software for builders. Generate accurate, professional construction quotes from drawings, photos or a job description in under 10 minutes.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Quoting Software for Builders", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Is there free quoting software for builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start from £29/month. Fully free tools do exist but typically lack UK-specific pricing, trade categories, or PDF export, making them unsuitable for professional quoting.",
      },
    },
    {
      "@type": "Question",
      "name": "What quoting software do builders use?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Builders use a range of tools from spreadsheets to dedicated software. Common options include The Proper Tool (built specifically for UK residential construction), Tradify, simPRO, and Buildertrend. Many sole traders still rely on Excel or Word templates, but purpose-built quoting software is faster, more accurate, and produces a more professional result.",
      },
    },
    {
      "@type": "Question",
      "name": "How much does builder quoting software cost in the UK?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "UK builder quoting software ranges from £29 to £100+ per month depending on features and quote volume. The Proper Tool starts at £29/month for sole traders (10 quotes/month) and goes up to £99/month for the Pro plan (50 quotes/month, drawing analysis, learned adjustments). Enterprise job-management platforms like simPRO can cost significantly more.",
      },
    },
    {
      "@type": "Question",
      "name": "Can quoting software read structural drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool can read uploaded PDFs of structural drawings and architect's plans. It extracts beam sizes, spans, connection details, and project dimensions from the drawings and uses them to generate a more accurate, fully itemised quote. This is a Pro-plan feature.",
      },
    },
    {
      "@type": "Question",
      "name": "Does quoting software for builders work for sole traders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool is built for builders of all sizes, including sole traders and small builders. The Lite plan at £29/month is sized for a one-person operation: 10 AI quotes per month, unlimited template and manual quotes, professional PDF export, and the full 31-category cost library. No complex setup, no per-seat fees.",
      },
    },
  ],
};

const FEATURES = [
  {
    icon: Zap,
    title: "UK pricing database",
    description: "462 rated tasks across 31 trade categories with current UK market rates. Low, Typical, and High bands built in from day one.",
  },
  {
    icon: FileText,
    title: "Reads structural drawings",
    description: "Upload PDFs of architect's drawings or structural plans. The tool extracts beam sizes, spans, and floor areas to price the job accurately.",
  },
  {
    icon: BookOpen,
    title: "31 trade categories",
    description: "Covers all common UK residential trades: groundworks, steelwork, carpentry, plastering, roofing, drainage, landscaping, and more.",
  },
  {
    icon: Star,
    title: "Branded PDF output",
    description: "Every quote exports as a professionally formatted PDF showing your company details, full line-item scope, pricing, and assumptions.",
  },
  {
    icon: PoundSterling,
    title: "10-minute quotes",
    description: "Describe the job, upload drawings, or choose a template. The AI generates a fully itemised scope and prices it, typically in under 10 minutes.",
  },
  {
    icon: CheckCircle2,
    title: "Expert validation",
    description: "Built-in Master Builder and Chartered Engineer review flags pricing outliers, scope gaps, and quantity concerns before you send.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Describe the job",
    description: "Type a description of the project, upload architect's drawings or structural plans, attach site photos, or a mix of all three. The Proper Tool works from whatever you have.",
  },
  {
    step: "2",
    title: "AI generates the scope",
    description: "The AI reads your inputs and produces a fully itemised scope of works: every trade, every line item, quantities and unit rates drawn from the UK pricing database.",
  },
  {
    step: "3",
    title: "Review, edit, and send",
    description: "Review every line, apply or dismiss validation flags, add or remove items, then export a branded PDF to send directly to your client.",
  },
];

const FAQS = [
  {
    q: "Is there free quoting software for builders?",
    a: "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start from £29/month. Fully free tools do exist but typically lack UK-specific pricing, trade categories, or PDF export, making them unsuitable for professional quoting.",
  },
  {
    q: "What quoting software do builders use?",
    a: "Builders use a range of tools from spreadsheets to dedicated software. Common options include The Proper Tool (built specifically for UK residential construction), Tradify, simPRO, and Buildertrend. Many sole traders still rely on Excel or Word templates, but purpose-built quoting software is faster, more accurate, and produces a more professional result.",
  },
  {
    q: "How much does builder quoting software cost in the UK?",
    a: "UK builder quoting software ranges from £29 to £100+ per month depending on features and quote volume. The Proper Tool starts at £29/month for sole traders (10 quotes/month) and goes up to £99/month for the Pro plan (50 quotes/month, drawing analysis, learned adjustments). Enterprise job-management platforms like simPRO can cost significantly more.",
  },
  {
    q: "Can quoting software read structural drawings?",
    a: "Yes. The Proper Tool can read uploaded PDFs of structural drawings and architect's plans. It extracts beam sizes, spans, connection details, and project dimensions from the drawings and uses them to generate a more accurate, fully itemised quote. This is a Pro-plan feature.",
  },
  {
    q: "Does quoting software for builders work for sole traders?",
    a: "Yes. The Proper Tool is built for builders of all sizes, including sole traders and small builders. The Lite plan at £29/month is sized for a one-person operation: 10 AI quotes per month, unlimited template and manual quotes, professional PDF export, and the full 31-category cost library. No complex setup, no per-seat fees.",
  },
];

export default function QuotingSoftwareForBuildersPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Quoting Software for Builders | The Proper Tool"
        description="Builder quoting software built for tradespeople. Generate professional, itemised quotes from drawings, photos or a job description in under 10 minutes. From £29/mo, 14-day free trial."
        canonical="/quoting-software-for-builders"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Quoting Software for Builders</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Quoting Software for Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Generate accurate, professional construction quotes from drawings, photos or a job description in under 10 minutes. Built for residential builders.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/comparisons">Compare alternatives</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · No credit card required</p>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="how-it-works">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">How it works</h2>
              <p className="text-lg text-muted-foreground">Three steps from job description to client-ready quote.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-8">
              {HOW_IT_WORKS.map((step) => (
                <div key={step.step} className="relative text-center">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg mb-4 mx-auto">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="features">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Everything a builder needs to quote faster</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">The Proper Tool is built around the UK construction market, not adapted from a generic SaaS platform built for other markets.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f) => (
                <div key={f.title} className="bg-background rounded-xl border border-border p-6">
                  <f.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Social proof */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-primary mb-2">10 min</p>
                <p className="text-muted-foreground">Average time to generate a full itemised quote</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">462</p>
                <p className="text-muted-foreground">UK construction rates across 31 trade categories</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">£29/mo</p>
                <p className="text-muted-foreground">Starting price, less than one hour of a builder's time</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing callout */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple, transparent pricing</h2>
              <p className="text-lg text-muted-foreground">Start with a 14-day free trial. No credit card required.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {([ 
                { name: "Lite", price: "£29", desc: "For sole traders", features: ["10 AI auto-quotes/mo", "Unlimited template & manual quotes", "Professional PDF export", "Full 31-category cost library"], highlight: false },
                { name: "Starter", price: "£59", desc: "For growing businesses", features: ["25 AI auto-quotes/mo", "Everything in Lite", "Pricing Intelligence", "Expert validation pipeline", "Regional pricing"], highlight: true },
                { name: "Pro", price: "£99", desc: "For high-volume builders", features: ["50 AI auto-quotes/mo", "Everything in Starter", "Drawing analysis", "Learned rate adjustments"], highlight: false },
              ] as { name: string; price: string; desc: string; features: string[]; highlight: boolean }[]).map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">{plan.price}<span className="text-base font-normal text-muted-foreground">/mo</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal links */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related guides</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/blog/how-to-price-building-jobs", label: "How to Price Building Jobs: A Practical Guide for UK Builders" },
                { href: "/blog/builder-quote-app-uk", label: "The Best Builder Quote App for UK Tradespeople" },
                { href: "/blog/free-quoting-software-builders-uk", label: "Free Quoting Software for Builders UK: What's Actually Available" },
                { href: "/quoting-app-for-builders", label: "Quoting App for Builders" },
                { href: "/quoting-software-for-contractors", label: "Quoting Software for Contractors" },
                { href: "/tradesman-quoting-tool", label: "Tradesman Quoting Tool" },
                { href: "/job-quoting-software", label: "Job Quoting Software" },
                { href: "/estimating-software-for-builders", label: "Estimating Software for Builders" },
                { href: "/free-trial/", label: "Free Trial" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to quote faster?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join builders using The Proper Tool to generate professional quotes in minutes, not hours.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free 14-day trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
